import { GiChemicalTank } from "react-icons/gi";
import { MdOutlineHeatPump } from "react-icons/md";
import { FaTachometerAlt, FaBoxOpen, FaBox, FaThermometerHalf, FaTerminal, FaRegImage } from "react-icons/fa";
import { TbEngine } from "react-icons/tb";

/**
 * COMPONENT_REGISTRY – Single source of truth for all SCADA component types.
 *
 * Keys MUST match the `value` field in `liveComponentsOptions` from dashboardUtils.jsx.
 * This ensures that components selected from the modal resolve correctly
 * when looking up metadata for config modals, node rendering, and backend storage.
 *
 * To add a new SCADA component:
 * 1. Add entry here (key = liveComponentsOptions value)
 * 2. Create *Node.jsx in ../nodes/ or use GenericNode
 * 3. Register in ../nodes/nodeTypes.js
 */
export const COMPONENT_REGISTRY = {
  // ── Primary SCADA Components ──────────────────────────────────────
  pumps: {
    type: "pumps",
    label: "Pumps",
    icon: MdOutlineHeatPump,
    subtitle: "Live Pumps Component",
    color: "#3b82f6",
    defaultWidth: 90,
    defaultHeight: 56,
    visualizations: ["value", "gauge", "graph"],
    defaultConfig: {
      visualization: "value",
      minValue: 0,
      maxValue: 100,
      unit: "RPM",
    },
    defaultStyles: { color: "#3b82f6", opacity: 1, designType: "realistic" },
  },
  conveyer: {
    type: "conveyer",
    label: "Conveyer",
    icon: FaBoxOpen,
    subtitle: "Live Conveyer Component",
    color: "#64748b",
    defaultWidth: 110,
    defaultHeight: 48,
    visualizations: ["value", "gauge", "graph"],
    defaultConfig: {
      visualization: "value",
      minValue: 0,
      maxValue: 100,
      unit: "RPM",
    },
    defaultStyles: { color: "#64748b", opacity: 1, designType: "realistic" },
  },
  motor: {
    type: "motor",
    label: "Motor",
    icon: TbEngine,
    subtitle: "Live Motor Component",
    color: "#8b5cf6",
    defaultWidth: 87,
    defaultHeight: 60,
    visualizations: ["value", "gauge", "graph"],
    defaultConfig: {
      visualization: "value",
      minValue: 0,
      maxValue: 100,
      unit: "RPM",
    },
    defaultStyles: { color: "#8b5cf6", opacity: 1, designType: "realistic" },
  },
  tanks: {
    type: "tanks",
    label: "Tanks",
    icon: GiChemicalTank,
    subtitle: "Live Tanks Component",
    color: "#06b6d4",
    defaultWidth: 69,
    defaultHeight: 100,
    visualizations: ["none", "graph"],
    defaultConfig: {
      visualization: "none",
      minValue: 0,
      maxValue: 100,
      unit: "%",
    },
    defaultStyles: { color: "#06b6d4", opacity: 1, designType: "realistic" },
  },
  chiller: {
    type: "chiller",
    label: "Chiller",
    icon: FaThermometerHalf,
    subtitle: "Live Chiller Component",
    color: "#0ea5e9",
    defaultWidth: 100,
    defaultHeight: 65,
    visualizations: ["value", "gauge", "graph"],
    defaultConfig: {
      visualization: "value",
      minValue: 0,
      maxValue: 100,
      unit: "°C",
    },
    defaultStyles: { color: "#0ea5e9", opacity: 1, designType: "realistic" },
  },
  ahu: {
    type: "ahu",
    label: "AHU",
    icon: FaBox,
    subtitle: "Live AHU Component",
    color: "#10b981",
    defaultWidth: 100,
    defaultHeight: 56,
    visualizations: ["value", "gauge", "graph"],
    defaultConfig: {
      visualization: "value",
      minValue: 0,
      maxValue: 100,
      unit: "°C",
    },
    defaultStyles: { color: "#10b981", opacity: 1, designType: "realistic" },
  },
  blowerFan: {
    type: "blowerFan",
    label: "Blower/Fan",
    icon: FaTachometerAlt,
    subtitle: "Live Blower/Fan Component",
    color: "#6366f1",
    defaultWidth: 77,
    defaultHeight: 70,
    visualizations: ["value", "gauge", "graph"],
    defaultConfig: {
      visualization: "value",
      minValue: 0,
      maxValue: 100,
      unit: "RPM",
    },
    defaultStyles: { color: "#6366f1", opacity: 1, designType: "realistic" },
  },
  battery: {
    type: "battery",
    label: "Battery",
    icon: FaBox,
    subtitle: "Live Battery Component",
    color: "#22c55e",
    defaultWidth: 96,
    defaultHeight: 58,
    visualizations: ["value", "gauge", "graph"],
    defaultConfig: {
      visualization: "value",
      minValue: 0,
      maxValue: 100,
      unit: "%",
    },
    defaultStyles: { color: "#22c55e", opacity: 1, designType: "realistic" },
  },
  msb: {
    type: "msb",
    label: "MSB",
    icon: FaBoxOpen,
    subtitle: "Live MSB Component",
    color: "#f97316",
    defaultWidth: 60,
    defaultHeight: 82,
    visualizations: ["value", "gauge", "graph"],
    defaultConfig: {
      visualization: "value",
      minValue: 0,
      maxValue: 100,
      unit: "V",
    },
    defaultStyles: { color: "#f97316", opacity: 1, designType: "realistic" },
  },
  transformer: {
    type: "transformer",
    label: "Transformer",
    icon: FaBox,
    subtitle: "Live Transformer Component",
    color: "#a855f7",
    defaultWidth: 84,
    defaultHeight: 70,
    visualizations: ["value", "gauge", "graph"],
    defaultConfig: {
      visualization: "value",
      minValue: 0,
      maxValue: 100,
      unit: "kW",
    },
    defaultStyles: { color: "#a855f7", opacity: 1, designType: "realistic" },
  },
  switchgear: {
    type: "switchgear",
    label: "Switchgear",
    icon: FaTerminal,
    subtitle: "Live Switchgear Component",
    color: "#ef4444",
    defaultWidth: 68,
    defaultHeight: 83,
    visualizations: ["value", "gauge", "graph"],
    defaultConfig: {
      visualization: "value",
      minValue: 0,
      maxValue: 100,
      unit: "A",
    },
    defaultStyles: { color: "#ef4444", opacity: 1, designType: "realistic" },
  },
  splitACIndoor: {
    type: "splitACIndoor",
    label: "Split A/C (indoor)",
    icon: FaThermometerHalf,
    subtitle: "Live Split A/C (indoor) Component",
    color: "#14b8a6",
    defaultWidth: 112,
    defaultHeight: 40,
    visualizations: ["value", "gauge", "graph"],
    defaultConfig: {
      visualization: "value",
      minValue: 0,
      maxValue: 100,
      unit: "°C",
    },
    defaultStyles: { color: "#14b8a6", opacity: 1, designType: "realistic" },
  },
  highbayLamp: {
    type: "highbayLamp",
    label: "Highbay Lamp",
    icon: FaBox,
    subtitle: "Live Highbay Lamp Component",
    color: "#eab308",
    defaultWidth: 85,
    defaultHeight: 70,
    visualizations: ["value", "gauge", "graph"],
    defaultConfig: {
      visualization: "value",
      minValue: 0,
      maxValue: 100,
      unit: "W",
    },
    defaultStyles: { color: "#eab308", opacity: 1, designType: "realistic" },
  },
  solarPanel: {
    type: "solarPanel",
    label: "Solar Panel",
    icon: FaRegImage,
    subtitle: "Live Solar Panel Component",
    color: "#f59e0b",
    defaultWidth: 100,
    defaultHeight: 48,
    visualizations: ["value", "gauge", "graph"],
    defaultConfig: {
      visualization: "value",
      minValue: 0,
      maxValue: 100,
      unit: "kW",
    },
    defaultStyles: { color: "#f59e0b", opacity: 1, designType: "realistic" },
  },
  solarInverter: {
    type: "solarInverter",
    label: "Solar Inverter",
    icon: FaBox,
    subtitle: "Live Solar Inverter Component",
    color: "#d97706",
    defaultWidth: 68,
    defaultHeight: 80,
    visualizations: ["value", "gauge", "graph"],
    defaultConfig: {
      visualization: "value",
      minValue: 0,
      maxValue: 100,
      unit: "kW",
    },
    defaultStyles: { color: "#d97706", opacity: 1, designType: "realistic" },
  },

  // ── Junction / Pipe Intersection ─────────────────────────────────
  junction: {
    type: "junction",
    label: "Junction",
    icon: FaBox,
    subtitle: "Pipe intersection / splitter node",
    color: "#64748b",
    defaultWidth: 40,
    defaultHeight: 40,
    visualizations: ["value"],
    defaultConfig: {
      visualization: "value",
    },
    defaultStyles: { color: "#64748b", opacity: 1, designType: "realistic" },
  },

  // ── Backward compatibility aliases ────────────────────────────────
  // Old data may have "pump", "tank", "valve", "splitAc" stored in the DB
  pump: {
    type: "pump",
    label: "Pump",
    icon: MdOutlineHeatPump,
    subtitle: "Centrifugal pump with flow indicator",
    color: "#3b82f6",
    defaultWidth: 90,
    defaultHeight: 56,
    visualizations: ["value", "gauge", "graph"],
    defaultConfig: { visualization: "value", minValue: 0, maxValue: 100, unit: "RPM" },
    defaultStyles: { color: "#3b82f6", opacity: 1, designType: "realistic" },
  },
  tank: {
    type: "tank",
    label: "Tank",
    icon: GiChemicalTank,
    subtitle: "Cylindrical tank with level indicator",
    color: "#06b6d4",
    defaultWidth: 69,
    defaultHeight: 100,
    visualizations: ["none", "graph"],
    defaultConfig: { visualization: "none", minValue: 0, maxValue: 100, unit: "%" },
    defaultStyles: { color: "#06b6d4", opacity: 1, designType: "realistic" },
  },
  splitAc: {
    type: "splitAc",
    label: "Split A/C (indoor)",
    icon: FaThermometerHalf,
    subtitle: "Live Split A/C (indoor) Component",
    color: "#14b8a6",
    defaultWidth: 112,
    defaultHeight: 40,
    visualizations: ["value", "gauge", "graph"],
    defaultConfig: { visualization: "value", minValue: 0, maxValue: 100, unit: "°C" },
    defaultStyles: { color: "#14b8a6", opacity: 1, designType: "realistic" },
  },
  valve: {
    type: "valve",
    label: "Valve",
    icon: FaTachometerAlt,
    subtitle: "Gate/butterfly valve with open/close state",
    color: "#f59e0b",
    defaultWidth: 80,
    defaultHeight: 64,
    visualizations: ["value", "gauge"],
    defaultConfig: { visualization: "value", minValue: 0, maxValue: 100, unit: "" },
    defaultStyles: { color: "#f59e0b", opacity: 1, designType: "realistic" },
  },
};

/**
 * Get a component entry by type with guaranteed fallback.
 * Prevents "undefined" label issues.
 */
export const getComponentMeta = (type) => {
  return COMPONENT_REGISTRY[type] || {
    type: type || "unknown",
    label: type ? type.charAt(0).toUpperCase() + type.slice(1) : "Component",
    icon: null,
    subtitle: "",
    color: "#94a3b8",
    defaultWidth: 80,
    defaultHeight: 80,
    visualizations: ["value"],
    defaultConfig: { visualization: "value", minValue: 0, maxValue: 100, fontSize: 14 },
    defaultStyles: { color: "#94a3b8", opacity: 1 },
  };
};

/**
 * Get the list of all component options for selection modals.
 * Returns array format matching the tile dashboard's componentsOptions pattern.
 */
export const getComponentOptions = () => {
  return Object.values(COMPONENT_REGISTRY).filter(
    (c) => !["pump", "tank", "valve", "splitAc", "junction"].includes(c.type)
  );
};

/**
 * Visualization type options for config forms.
 */
export const VISUALIZATION_OPTIONS = [
  { label: "None", value: "none" },
  { label: "Value Display", value: "value" },
  { label: "Gauge / Dial", value: "gauge" },
  { label: "Level Indicator", value: "level" },
  { label: "Mini Graph", value: "graph" },
];
